/**
 * La pestaña «Historial de cargas» de Público (DISENO §2): una fila por versión de la base (cuándo, quién, qué traía,
 * su tamaño y si está en uso o reemplazada), con su descarga y «Subir nueva versión». Siempre se usa la última; las
 * anteriores quedan para bajarlas. Lo que se baja es la base ya leída (las ventas y los clientes, en CSV), no el
 * archivo original (duda 6 de la fase 0). En la que está en uso, el consentimiento que declaró el comercio (fase 4).
 * Desde el 29/10/2026, en cada versión con la declaración, si tiene respaldo (y el equipo lo puede ver) o no: las de
 * antes, sin respaldo, siguen valiendo y se marcan «sin respaldo».
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Loader2, Upload } from "lucide-react";
import { baseVentasApi } from "../api";
import type { Version } from "../types";
import { R_SUBIR_BASE, cuandoSubida, quienSubio, tamanoVersion } from "../logic/base";
import { ConsentimientoDeLaBase } from "./ConsentimientoDeLaBase";
import { ChipRespaldo } from "./LoQueCambiaLaDeclaracion";
import { usePedido } from "../../emails/hooks/usePedido";
import { leerError, textoError } from "../../wa-oficial/logic/respuesta";
import { ErrorTarjeta, Esqueleto, Vacio } from "../../wa-oficial/components/estadisticas/comun";

const ARCHIVO_TEXTO = { pedidos: "Ventas", clientes: "Clientes" } as const;

export function HistorialCargas() {
  const h = usePedido<Version[]>(() => baseVentasApi.historial(), "historial");
  const [bajando, setBajando] = useState("");
  const [aviso, setAviso] = useState("");
  const numeroDe = (id: string) => h.dato?.find((x) => x.id === id)?.numero ?? null;

  const bajar = async (v: Version, archivo: "pedidos" | "clientes") => {
    setBajando(`${v.id}:${archivo}`);
    setAviso("");
    try {
      await baseVentasApi.descargar(v, archivo);
    } catch (e) {
      // `descargarBinario` lanza un Error con el texto del backend; sin conexión, fetch lanza un TypeError en inglés
      setAviso(e instanceof Error && !(e instanceof TypeError) && e.message ? e.message : textoError(leerError(e)));
    } finally {
      setBajando("");
    }
  };

  return (
    <div data-testid="historial-cargas">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
        <p className="text-sm text-slate-600">Siempre usamos la última. Las anteriores quedan guardadas acá.</p>
        <Link to={R_SUBIR_BASE} className="min-h-[44px] px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-violet-700">
          <Upload size={15} aria-hidden="true" /> Subir nueva versión
        </Link>
      </div>
      {aviso && <p role="alert" className="mb-3 text-sm text-red-700 bg-red-50 rounded-xl px-4 py-2">{aviso}</p>}
      {h.error && !h.dato ? (
        <ErrorTarjeta texto={h.error} onReintentar={h.recargar} />
      ) : !h.dato ? (
        <Esqueleto alto="h-32" etiqueta="Cargando el historial" />
      ) : h.dato.length === 0 ? (
        <Vacio>Todavía no se subió ninguna base de ventas.</Vacio>
      ) : (
        <ul>
          {h.dato.map((v) => (
            <li key={v.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4 border-t border-slate-100">
              <span className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500" aria-hidden="true">
                <FileText size={18} />
              </span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-slate-900">{cuandoSubida(v.subidaAt)}</p>
                <p className="text-sm text-slate-500">Ventas y clientes · subió {quienSubio(v)}</p>
              </div>
              <span className="text-sm text-slate-700 sm:min-w-[220px]">{tamanoVersion(v)}</span>
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${v.estado === "en_uso" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {v.estado === "en_uso" ? "En uso" : "Reemplazada"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Descargar</span>
                {(["pedidos", "clientes"] as const).map((a) => (
                  <button
                    key={a}
                    type="button"
                    disabled={!v.descargas[a] || !!bajando}
                    onClick={() => void bajar(v, a)}
                    aria-label={`Descargar ${ARCHIVO_TEXTO[a].toLowerCase()} de la versión ${v.numero}`}
                    className="min-h-[44px] px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {bajando === `${v.id}:${a}` ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {ARCHIVO_TEXTO[a]}
                  </button>
                ))}
              </div>
              {/* el consentimiento declarado vale para la base en uso, que es con la que se prepara el envío */}
              {v.estado === "en_uso" && <ConsentimientoDeLaBase v={v} alCambiar={h.recargar} numeroDe={numeroDe} />}
              {/* en una reemplazada, la declaración que tuvo: con o sin respaldo (la constancia de lo que se mandó con ella) */}
              {v.estado !== "en_uso" && v.consentimiento && (
                <div className="w-full flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid={`consentimiento-version-${v.numero}`}>
                  <span>Consentimiento declarado: «{v.consentimiento.como}»{v.consentimiento.heredadaDe ? " (heredado)" : ""}</span>
                  <ChipRespaldo c={v.consentimiento} versionId={v.id} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
